<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.fetch_quests', 2),
                        href: meta.links.index,
                    },
                    {
                        name: fetchQuest.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <!--        <img :src="fetchQuest.image_path" alt="" class="w-full absolute object-contain object-cover" />-->
        <div
            class="bg-gray-50 overflow-hidden sm:rounded-lg md:aspect-video md:aspect-w-16 md:aspect-h-9 bg-no-repeat bg-center bg-cover"
            :style="style"
        >
            <div class="flex flex-col justify-end">
                <div class="p-4" v-if="fetchQuest.is_reward_shown">
                    <div class="">
                        {{ $t('messages.prize_to_be_shared') }}
                        ({{
                            fetchQuest.is_reward_evenly_split
                                ? $t('messages.reward_evenly_split')
                                : $t('messages.reward_not_evenly_split')
                        }}) :
                    </div>
                    <div class="flex space-x-2 mt-3">
                        <div>
                            <img class="h-6 w-6" src="/assets/gamification/currency/coin.svg" alt="emerald" />
                        </div>
                        <div class="">
                            {{ fetchQuest.coins_amount }}
                        </div>
                    </div>
                </div>

                <div class="mt-5 px-4">
                    <div class="grid grid-cols-2 lg:grid-cols-6 gap-6">
                        <div v-for="item in items" class="bg-gray-100 p-2 rounded-lg" :key="item.id">
                            <img :src="item.image_path" alt="" />
                            <div>
                                {{ item.name }}
                            </div>
                            <div class="text-gray-600 text-sm">
                                {{ item.options_description }}
                            </div>
                            <div v-if="item.description">
                                <Tippy>
                                    <button>
                                        <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                    </button>
                                    <template #content>
                                        {{ item.description }}
                                    </template>
                                </Tippy>
                            </div>
                            <div>
                                <ProgressBar
                                    :percent="Math.min(100, Math.round((item.items_given_count / item.quantity) * 100))"
                                    class="w-full"
                                    color="indigo"
                                >
                                    {{ item.items_given_count }} / {{ item.quantity }}
                                </ProgressBar>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="py-5 sm:p-6" v-if="fetchQuest.description">
                    <AppCard>
                        <p v-html="fetchQuest.description" />
                    </AppCard>
                </div>
            </div>
        </div>

        <div class="rounded-md bg-blue-50 p-4 mt-5">
            <div class="flex">
                <div class="shrink-0">
                    <!-- Heroicon name: solid/information-circle -->
                    <svg
                        class="h-5 w-5 text-blue-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
                <div class="ml-3 flex-1 md:flex md:justify-between">
                    <p class="text-sm text-blue-700">
                        {{ fetchQuest.event_ending_date_message }}
                    </p>
                </div>
            </div>
        </div>

        <div v-if="fetchQuest.max_actions_per_student" class="mt-3">
            {{
                $t('messages.this_event_allows_X_actions', {
                    count: fetchQuest.max_actions_per_student,
                })
            }}
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.actions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.when') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(action, index) in fetchQuest.actions"
                                :key="action.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex space-x-1 items-center">
                                        <img
                                            :src="action.avatar_or_default"
                                            :alt="action.full_name"
                                            :title="action.full_name"
                                            class="rounded-full h-12"
                                        />
                                        <div>{{ action.full_name }}</div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <img :src="action.action_image" class="h-12" />
                                    <div>
                                        {{ action.name }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ action.diffForHumans }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div v-if="fetchQuest.currencyPoints" class="mt-5">
            <h2>
                {{ $tc('messages.points', 2) }}
            </h2>

            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $t('messages.student') }}
                                    </th>
                                    <th
                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.points', 2) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(currencyPoint, index) in fetchQuest.currencyPoints"
                                    :key="currencyPoint.id"
                                >
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <img
                                            :src="currencyPoint.avatar_or_default"
                                            :alt="currencyPoint.student_name"
                                            :title="currencyPoint.student_name"
                                            class="rounded-full h-12"
                                        />
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                        <div class="flex space-x-1">
                                            <div>
                                                <BadgePill color="green" v-if="currencyPoint.points">
                                                    {{ currencyPoint.points }}
                                                </BadgePill>
                                            </div>

                                            <div>
                                                <BadgePill color="yellow" v-if="currencyPoint.coins_points">
                                                    {{ currencyPoint.coins_points }}
                                                </BadgePill>
                                            </div>

                                            <div>
                                                <BadgePill color="red" v-if="currencyPoint.health_points">
                                                    {{ currencyPoint.health_points }}
                                                </BadgePill>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import AppCard from '@/Shared/AppCard.vue';
import { Tippy } from 'vue-tippy';
import { IconInfoCircle } from '@tabler/icons-vue';

export default {
    components: {
        AppCard,
        Tippy,
        IconInfoCircle,
        PanelPage,
        BadgePill,
        ProgressBar,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'fetchQuest', 'items', 'gamificationSet'],
    data() {
        return {};
    },
    computed: {
        percent: function () {
            return Math.floor((this.fetchQuest.enemy_health_left / this.fetchQuest.enemy_health_total) * 100);
        },
        style: function () {
            let styleObject = {};
            if (this.fetchQuest.image_path) {
                styleObject.backgroundImage = `url(${this.fetchQuest.image_path})`;
            }
            return styleObject;
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
